import { useCallback, useContext, useMemo } from "react";
import { AppContext } from "../context/AppContext";

/* ────────────────────────────────────────────────────────────
 * usePermissions — the page-side twin of the sidebar's own `can` /
 * `canAny` (sharedComponent/Sidebar/useSidebar.js).
 *
 * There is one permission source in the app: the flat
 * { "Permission Name": true } map the backend returns from
 * GET_USER_PERMISSIONS. AppState keeps it in context and mirrors it to
 * localStorage("Permissions"), and refreshes both whenever the socket
 * reports an "access-view" change. Context is read first so a role
 * edit applies without a reload; localStorage is the fallback for
 * components that mount outside the provider.
 *
 * Semantics match the sidebar exactly, so a row and the screen behind
 * it can never disagree:
 *   can(key)      false when `key` is missing (an undeclared gate is
 *                 NOT an implicit yes — call sites spell that out as
 *                 `!item.permission || can(item.permission)`).
 *                 An array is treated as "any of".
 *   canAny(keys)  true for an empty / absent list.
 *   allowed(list) drops the entries of a config list whose own
 *                 `permission` the user lacks; entries without one are
 *                 kept. This is what the sidebar does to its items, and
 *                 what the CRM tab strips, "+ Add" menu and quick-action
 *                 tiles now do to theirs.
 * ──────────────────────────────────────────────────────────── */

const readStoredPermissions = () => {
  try {
    return JSON.parse(localStorage.getItem("Permissions")) || {};
  } catch {
    return {};
  }
};

export const usePermissions = () => {
  const ctx = useContext(AppContext);

  const permissions = useMemo(() => {
    const fromContext = ctx?.permissions;
    if (fromContext && Object.keys(fromContext).length > 0) return fromContext;
    return readStoredPermissions();
  }, [ctx?.permissions]);

  const can = useCallback(
    (key) => {
      if (!key) return false;
      if (Array.isArray(key)) return key.some((k) => Boolean(permissions[k]));
      return Boolean(permissions[key]);
    },
    [permissions]
  );

  const canAny = useCallback(
    (keys) => {
      if (!keys || keys.length === 0) return true;
      return keys.some((k) => Boolean(permissions[k]));
    },
    [permissions]
  );

  const allowed = useCallback(
    (list) => (list || []).filter((e) => !e?.permission || can(e.permission)),
    [can]
  );

  return { permissions, can, canAny, allowed };
};

export default usePermissions;
